import type { Buff } from './buffer'

// AWS specifies this must be at least 5MB
export const chunkSize = 1024 * 1024 * 10 // 10MiB

export type ChunkReader = (chunkIndex: number) => Promise<ArrayBuffer>
export type ChunkWriter = (chunkIndex: number, chunk: ArrayBuffer) => Promise<void>

export function getTotalChunks(totalBytes: number) {
    return Math.ceil(totalBytes / chunkSize)
}

export class ChunkOutOfBounds extends Error {
    constructor(
        public chunkIndex: number,
        public totalBytes: number
    ) {
        super('Chunk index out of bounds')
    }
}

export async function streamChunks(totalBytes: number, reader: ChunkReader, writer: ChunkWriter) {
    const totalChunks = getTotalChunks(totalBytes)
    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
        const chunk = await reader(chunkIndex)
        await writer(chunkIndex, chunk)
    }
}

export function getSliceBoundaries(chunkIndex: number, totalBytes: number) {
    const start = chunkIndex * chunkSize
    const end = (chunkIndex + 1) * chunkSize

    // Throw if past the end
    if (start >= totalBytes) {
        throw new ChunkOutOfBounds(chunkIndex, totalBytes)
    }

    return {
        start,
        end: Math.min(end, totalBytes), // If last one, truncate to end
    }
}

export function chunkReaderForBuffer(buffer: Buff): ChunkReader {
    return async (chunkIndex) => {
        const { start, end } = getSliceBoundaries(chunkIndex, buffer.byteLength)
        return buffer.slice(start, end)
    }
}
